import { Fragment, useRef, useState, type RefObject } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { snowflakeApi } from '../../api/snowflake'
import { RefusedError } from '../../api/client'
import { LineIcon } from '../../components/LineIcon'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import type { SnowflakeDisconnected, SnowflakeEntity, SnowflakeKey, SnowflakeLanded, SnowflakeSettings, SnowflakeStep, SnowflakeTest, SnowflakeView } from '../../types/snowflake'
import { rowsLabel, settingsChanged, SNOWFLAKE_FIELDS, statusLabel, statusOf, testPassed, testSentence, type SnowflakeStatus } from '../../lib/snowflake'
import './snowflake.css'

// THE SNOWFLAKE CONNECTOR (owner, 2026-10-01: "add snowflake connector including ability to generate keypair with
// instruction to set up on the other side"). A firm system: one connection for the spoke, made by an admin. The
// admin names the account and the service user; the server generates the key pair and keeps the private half; this
// page shows the PUBLIC half and the exact statements for whoever administers Snowflake, then tests the round trip.
// Nothing here decides anything: the steps, the scripts, the verdict of a test and the reason a switch is refused
// are all the API's words. No response this page reads carries a private key, so there is none to show.

const message = (err: unknown) => (err instanceof RefusedError ? err.refusal.message : err instanceof Error ? err.message : 'Unavailable')

const STATUS_ICON: Record<SnowflakeStatus, string> = { 'not-set-up': 'circle', waiting: 'warning', connected: 'check' }

export function SnowflakeConnector() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const viewKey = ['snowflake', spoke]
  const q = useQuery({ queryKey: viewKey, queryFn: () => snowflakeApi.view(spoke), retry: false, staleTime: 15_000 })

  // What the person has typed and not saved. Null means the form shows what is saved (or the API's suggestions).
  const [draft, setDraft] = useState<SnowflakeSettings | null>(null)
  const [confirming, setConfirming] = useState(false)
  // What disconnecting handed back: the steps left to do on the Snowflake side. Kept until dismissed or reconnected.
  const [gone, setGone] = useState<SnowflakeDisconnected | null>(null)

  const landscapeMoved = () => void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
  const show = (view: SnowflakeView) => { qc.setQueryData(viewKey, view); landscapeMoved() }
  // A test changes what the view says (verified, the last error), so the view is read again after every one.
  const reread = () => { void qc.invalidateQueries({ queryKey: viewKey }); landscapeMoved() }

  const test = useMutation({ mutationFn: () => snowflakeApi.test(spoke), onSettled: reread })
  const testNext = useMutation({ mutationFn: () => snowflakeApi.test(spoke, true), onSettled: reread })
  const list = useMutation({ mutationFn: () => snowflakeApi.entities(spoke) })
  // what has landed (PL-206), and the press that lands it now
  const landed = useQuery({ queryKey: ['snowflake-landed', spoke], queryFn: () => snowflakeApi.landed(spoke), refetchInterval: 20_000 })
  const sync = useMutation({ mutationFn: () => snowflakeApi.sync(spoke) })
  // A result on screen is about the names and the key it was run with; once either changes it is no longer true.
  const forget = () => { test.reset(); testNext.reset(); list.reset(); promote.reset() }
  const save = useMutation({ mutationFn: (settings: SnowflakeSettings) => snowflakeApi.save(spoke, settings), onSuccess: (view) => { show(view); setDraft(null); setGone(null); forget() } })
  const newKey = useMutation({ mutationFn: () => snowflakeApi.newKey(spoke), onSuccess: (view) => { show(view); forget() } })
  const promote = useMutation({ mutationFn: () => snowflakeApi.promoteKey(spoke), onSuccess: (view) => { show(view); testNext.reset() } })
  const discard = useMutation({ mutationFn: () => snowflakeApi.discardNextKey(spoke), onSuccess: (view) => { show(view); testNext.reset(); promote.reset() } })
  const disconnect = useMutation({
    mutationFn: () => snowflakeApi.disconnect(spoke),
    onSuccess: (left) => {
      setGone(left); setConfirming(false); setDraft(null); forget(); save.reset(); newKey.reset(); discard.reset()
      // back to the beginning: the view is read again and comes back not configured, with the suggestions
      void qc.resetQueries({ queryKey: viewKey })
      landscapeMoved()
    },
  })

  const v = q.data
  // A refusal (not an admin) shows the reason and nothing else; so does a first read that failed outright.
  const blocked = q.isError && (!v || q.error instanceof RefusedError)

  return (
    <section className="page snowflake">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · <Link to="/connectors">Import Data</Link></p>
          <h1>Snowflake</h1>
          {!blocked && (
            <p className="lede">
              One read-only connection for the whole firm. Scout generates the key pair and keeps the private half; your
              Snowflake administrator runs a few statements to let the public half in. Scout reads from Snowflake and
              never writes to it.
            </p>
          )}
        </div>
      </header>

      {q.isPending && <p className="muted">Loading…</p>}
      {blocked && <div className="refusal" role="status"><strong>{q.error instanceof RefusedError ? 'Not yours to connect' : 'Unavailable'}</strong><p>{message(q.error)}</p></div>}
      {q.isError && !blocked && <p className="error">{message(q.error)}</p>}

      {gone && !blocked && (
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Snowflake is disconnected</h2>
              <p>Scout has deleted its keys and can no longer sign in. What is left is on the Snowflake side: hand these to whoever administers it.</p>
            </div>
            <button type="button" className="link-button" onClick={() => setGone(null)}>Dismiss</button>
          </div>
          <SnowflakeSteps steps={gone.cleanup} script={gone.script} fileName="scout-snowflake-cleanup.sql" />
        </div>
      )}

      {v && !blocked && (() => {
        const status = statusOf(v)
        const values = draft ?? v.settings
        const changed = settingsChanged(v.settings, values)
        return (
          <>
            <p className={`snowflake-status ${status}`} role="status">
              <span className={`tag ${status}`}><LineIcon name={STATUS_ICON[status]} size={14} />{statusLabel(status)}</span>
              <span>
                {status === 'connected'
                  ? `Snowflake answered${v.verifiedAs ? ` as ${v.verifiedAs}` : ''}${v.verifiedAt ? `, ${formatDate(v.verifiedAt)}` : ''}.`
                  : status === 'waiting'
                    ? 'The public key is not on the user yet, or the test has not been run.'
                    : 'Name the connection below. Saving it generates the key.'}
              </span>
            </p>
            {v.lastTestError && (
              <p className="snowflake-last-test">
                <strong>The last test{v.lastTestAt ? `, ${formatDate(v.lastTestAt)},` : ''} failed.</strong> {v.lastTestError}
                {v.verified && ' The connection stays on: one failed test does not undo a key Snowflake has accepted.'}
              </p>
            )}

            <form className="card" onSubmit={(e) => { e.preventDefault(); save.mutate(values) }}>
              <div className="card-head">
                <div>
                  <h2>1 · The connection</h2>
                  <p>
                    Where your Snowflake is, who Scout signs in as, and what that user may use and read.
                    {v.configured ? ' The steps below are written with these names.' : ' Saving these generates the key pair and writes the steps for the Snowflake side with your own names in them.'}
                  </p>
                </div>
              </div>
              <div className="snowflake-form">
                {SNOWFLAKE_FIELDS.map((f) => (
                  <label key={f.key}>
                    {f.label}
                    <input
                      name={f.key}
                      value={values[f.key]}
                      placeholder={f.placeholder}
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      onChange={(e) => setDraft({ ...values, [f.key]: e.target.value })}
                    />
                    <span className="snowflake-hint">{f.hint}</span>
                  </label>
                ))}
              </div>
              {save.isError && <p className="snowflake-refusal" role="alert">{message(save.error)}</p>}
              <div className="actions">
                <button type="submit" className="button primary" disabled={save.isPending || (v.configured && !changed)}>
                  {save.isPending ? 'Saving…' : v.configured ? 'Save' : 'Save and generate the key'}
                </button>
                {v.configured && changed && <button type="button" className="link-button" onClick={() => { setDraft(null); save.reset() }}>Undo changes</button>}
              </div>
              {v.host && <p className="muted small snowflake-host">Scout calls <code>{v.host}</code>.</p>}
              {v.configured && (
                <p className="muted small">
                  Changing the account or the service user moves the key to a different user, so the connection goes back to
                  waiting until a test passes. The key itself is kept.
                </p>
              )}
            </form>

            {v.verified && (
              <div className="card">
                <div className="card-head">
                  <div>
                    <h2>What has landed</h2>
                    <p>
                      The tables the deployment names are read whole every night and on this press, and land as rows Scout then maps onto
                      accounts, people and opportunities. PitchBook columns land with them and are kept apart as their own source.
                    </p>
                  </div>
                  <button type="button" className="button primary" onClick={() => sync.mutate()} disabled={sync.isPending}>{sync.isPending ? 'Starting…' : 'Land the tables now'}</button>
                </div>
                {sync.isError && <p className="snowflake-refusal" role="alert">{message(sync.error)}</p>}
                {sync.data && <p className="muted small">Reading now · <Link to={`/jobs/${encodeURIComponent(sync.data.jobId)}`}>open the console</Link>. This card refreshes as rows land.</p>}
                {landed.data && <LandedTable rows={landed.data.entities} />}
              </div>
            )}

            {v.key && (
              <div className="card">
                <div className="card-head">
                  <div>
                    <h2>2 · On the Snowflake side</h2>
                    <p>
                      Scout can generate a key; only a Snowflake administrator can put it on a user. These steps can be handed
                      to whoever administers Snowflake, who never needs to open Scout. The public key in them is safe to email.
                    </p>
                  </div>
                </div>
                <SnowflakeSteps steps={v.setup} script={v.script} fileName="scout-snowflake-setup.sql" />
                <KeyFacts k={v.key} user={v.settings.user} callsFrom={v.callsFrom} />
                <p className="muted small snowflake-private">The private key was generated inside Scout and is never shown, here or anywhere.</p>
              </div>
            )}

            {v.key && (
              <div className="card">
                <div className="card-head">
                  <div>
                    <h2>3 · Test the connection</h2>
                    <p>One round trip: Scout signs in with its key and asks Snowflake who it is. Run it once the steps above are done. A pass turns the connection on.</p>
                  </div>
                  <button type="button" className="button primary" onClick={() => test.mutate()} disabled={test.isPending}>{test.isPending ? 'Asking Snowflake…' : 'Test connection'}</button>
                </div>
                {changed && <p className="muted small">The test uses the names that are saved, not the changes you have not saved yet.</p>}
                {test.isError && <p className="snowflake-refusal" role="alert">{message(test.error)}</p>}
                {test.data && <SnowflakeTestResult test={test.data} />}
                {!test.data && !test.isPending && v.lastTestAt && <p className="muted small">Last tested {formatDate(v.lastTestAt)}.</p>}
              </div>
            )}

            {v.verified && (
              <div className="card">
                <div className="card-head">
                  <div>
                    <h2>What Scout can see</h2>
                    <p>
                      The tables and views in <code>{v.settings.database}.{v.settings.schema}</code> that role <code>{v.settings.role}</code> can
                      read. Nothing is read from them yet: this proves what the role can see, and nothing more.
                    </p>
                  </div>
                  <button type="button" className="button" onClick={() => list.mutate()} disabled={list.isPending}>{list.isPending ? 'Asking Snowflake…' : 'List tables and views'}</button>
                </div>
                {list.isError && <p className="snowflake-refusal" role="alert">{message(list.error)}</p>}
                {list.data && (list.data.entities.length === 0
                  ? <p className="muted">The role sees no tables or views in this schema. The SELECT grants in the steps above are what let it see them.</p>
                  : <EntityTable entities={list.data.entities} />)}
              </div>
            )}

            {v.key && (
              <div className="card">
                <div className="card-head">
                  <div>
                    <h2>Keys</h2>
                    <p>
                      Before Snowflake has accepted a key, a new pair <strong>replaces</strong> the one above, and step 2 has to be run
                      again with the new public key. On a working connection it starts a <strong>rotation</strong> instead: the new key
                      goes in the user's other slot, and nothing stops working while it is installed.
                    </p>
                    <p className="muted small">
                      {v.nextKey
                        ? 'A new key is already waiting. Generating another replaces the one waiting, not the one in use.'
                        : v.verified
                          ? 'This connection is working, so a new pair starts a rotation.'
                          : 'Snowflake has not accepted this connection yet, so a new pair replaces the key.'}
                    </p>
                  </div>
                  <button type="button" className="button" onClick={() => newKey.mutate()} disabled={newKey.isPending}>{newKey.isPending ? 'Generating…' : 'Generate a new key pair'}</button>
                </div>
                {newKey.isError && <p className="snowflake-refusal" role="alert">{message(newKey.error)}</p>}

                {v.nextKey && (
                  <div className="snowflake-rotation">
                    <h3>A new key is waiting</h3>
                    <p>The connection keeps working on the current key. Install the new one with these steps, test it, then switch.</p>
                    <SnowflakeSteps steps={v.rotation} script={v.rotationScript} fileName="scout-snowflake-rotation.sql" />
                    <KeyFacts k={v.nextKey} user={v.settings.user} callsFrom={v.callsFrom} />
                    <div className="actions">
                      <button type="button" className="button" onClick={() => testNext.mutate()} disabled={testNext.isPending}>{testNext.isPending ? 'Asking Snowflake…' : 'Test the new key'}</button>
                      <button type="button" className="button primary" onClick={() => promote.mutate()} disabled={promote.isPending}>{promote.isPending ? 'Switching…' : 'Switch to the new key'}</button>
                      <button type="button" className="button" onClick={() => discard.mutate()} disabled={discard.isPending}>{discard.isPending ? 'Discarding…' : 'Discard the new key'}</button>
                    </div>
                    {testNext.isError && <p className="snowflake-refusal" role="alert">{message(testNext.error)}</p>}
                    {testNext.data && <SnowflakeTestResult test={testNext.data} />}
                    {promote.isError && <p className="snowflake-refusal" role="alert">{message(promote.error)}</p>}
                    {discard.isError && <p className="snowflake-refusal" role="alert">{message(discard.error)}</p>}
                  </div>
                )}
              </div>
            )}

            {v.configured && (
              <div className="snowflake-disconnect">
                {confirming ? (
                  <div className="snowflake-confirm">
                    <strong>Are you sure? This deletes Scout's keys.</strong>
                    <button type="button" className="button danger small" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>{disconnect.isPending ? 'Disconnecting…' : 'Confirm'}</button>
                    <button type="button" className="button small" onClick={() => { setConfirming(false); disconnect.reset() }} disabled={disconnect.isPending}>Cancel</button>
                  </div>
                ) : (
                  <button type="button" className="link-button" onClick={() => setConfirming(true)}>Disconnect Snowflake</button>
                )}
                {disconnect.isError && <p className="snowflake-refusal" role="alert">{message(disconnect.error)}</p>}
              </div>
            )}
          </>
        )
      })()}
    </section>
  )
}

/** The steps for the other side, one renderer for all three lists: the setup, a rotation, the cleanup after a
 *  disconnect. Each step is its title, why, and its statement with a Copy of its own; above them, the whole list as
 *  one script to copy or download. */
export function SnowflakeSteps({ steps, script, fileName }: { steps: SnowflakeStep[]; script: string | null; fileName: string }) {
  const listRef = useRef<HTMLOListElement>(null)
  return (
    <>
      {script && (
        <div className="actions snowflake-script">
          <CopyButton text={script} label="Copy all as one script" fallback={listRef} />
          <button type="button" className="button small" onClick={() => downloadText(script, fileName)}>Download as .sql</button>
        </div>
      )}
      <ol className="snowflake-steps" ref={listRef}>
        {steps.map((step, i) => <Step key={i} n={i + 1} step={step} />)}
      </ol>
    </>
  )
}

function Step({ n, step }: { n: number; step: SnowflakeStep }) {
  const codeRef = useRef<HTMLElement>(null)
  return (
    <li className="snowflake-step">
      <span className="snowflake-step-no" aria-hidden="true">{n}</span>
      <div className="snowflake-step-body">
        <h3>{step.title}</h3>
        <p>{step.detail}</p>
        {step.sql != null && (
          <div className="snowflake-sql">
            <div className="snowflake-sql-head">
              <span>Run in Snowflake</span>
              <CopyButton text={step.sql} fallback={codeRef} />
            </div>
            <pre><code ref={codeRef}>{step.sql}</code></pre>
          </div>
        )}
      </div>
    </li>
  )
}

/** A key as facts: the public half (wrapped, never widening the card), its fingerprint, where it goes, when it was made. */
function KeyFacts({ k, user, callsFrom }: { k: SnowflakeKey; user: string; callsFrom?: string | null }) {
  const keyRef = useRef<HTMLElement>(null)
  const printRef = useRef<HTMLElement>(null)
  const fromRef = useRef<HTMLElement>(null)
  return (
    <dl className="facts snowflake-key">
      <dt>Public key</dt>
      <dd><code className="snowflake-keytext" ref={keyRef}>{k.publicKey}</code><CopyButton text={k.publicKey} fallback={keyRef} /></dd>
      <dt>Fingerprint</dt>
      <dd><code ref={printRef}>{k.fingerprint}</code><CopyButton text={k.fingerprint} fallback={printRef} /></dd>
      <dt>Goes in</dt>
      <dd><span><code>{k.property}</code> on user <code>{user}</code>, slot {k.slot} of the two a Snowflake user holds</span></dd>
      <dt>Generated</dt>
      <dd>{k.createdAt ? formatDate(k.createdAt) : '—'}</dd>
      <dt>Scout calls from</dt>
      <dd>{callsFrom
        ? <><code ref={fromRef}>{callsFrom}</code><CopyButton text={callsFrom} fallback={fromRef} /><span className="muted small"> the address a Snowflake network policy has to allow</span></>
        : <span className="muted small">The server could not find its own public address; ask whoever runs its network.</span>}</dd>
    </dl>
  )
}

/** What one test found. A pass says who Snowflake thinks Scout is, and a hint beside a pass is a missing grant. A
 *  failure is Snowflake's own words first, then the step that fixes it. */
export function SnowflakeTestResult({ test }: { test: SnowflakeTest }) {
  const passed = testPassed(test)
  return (
    <div className={`snowflake-result ${passed ? 'passed' : 'failed'}`} role="status">
      {passed ? (
        <>
          <p><LineIcon name="check" /> {testSentence(test)}.</p>
          {test.version && <p className="muted small">Snowflake version {test.version}</p>}
          {test.hint && <p className="snowflake-fix missing"><LineIcon name="warning" className="warn" /> <strong>A grant is missing.</strong> {test.hint}</p>}
        </>
      ) : (
        <>
          <p className="muted small">{test.reachable ? `Snowflake answered in ${test.latencyMs.toLocaleString('en-US')} ms and refused. In its own words:` : 'Snowflake did not answer.'}</p>
          <p className="said">{testSentence(test)}</p>
          {test.hint && <p className="snowflake-fix"><strong>What fixes it.</strong> {test.hint}</p>}
        </>
      )}
    </div>
  )
}

/** The tables and views the role can see; a name opens its columns. */
function LandedTable({ rows }: { rows: SnowflakeLanded[] }) {
  if (rows.length === 0) return <p className="muted">Nothing has landed yet.</p>
  return (
    <table className="snowflake-entities">
      <thead><tr><th>Table</th><th className="num">Rows</th><th>Last landed</th><th>Last read</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.entity}>
            <td><code>{r.entity}</code></td>
            <td className="num">{r.rows.toLocaleString()}</td>
            <td>{r.lastLoadedAt ? formatDate(r.lastLoadedAt) : '—'}</td>
            <td className="muted small">{r.lastOutcome ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function EntityTable({ entities }: { entities: SnowflakeEntity[] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (name: string) => setOpen((was) => {
    const next = new Set(was)
    if (!next.delete(name)) next.add(name)
    return next
  })
  return (
    <table className="grid snowflake-entities">
      <thead>
        <tr><th>Table or view</th><th className="num">Estimated rows</th><th className="num">Columns</th></tr>
      </thead>
      <tbody>
        {entities.map((entity) => {
          const isOpen = open.has(entity.name)
          return (
            <Fragment key={entity.name}>
              <tr>
                <td>
                  <button type="button" className="link-button entity-name" aria-expanded={isOpen} onClick={() => toggle(entity.name)}>{entity.name}</button>
                </td>
                <td className="num">{rowsLabel(entity.estimatedRows)}</td>
                <td className="num">{entity.columns.length.toLocaleString('en-US')}</td>
              </tr>
              {isOpen && (
                <tr className="snowflake-columns">
                  <td colSpan={3}>
                    <table>
                      <thead><tr><th>Column</th><th>Type</th><th>Nullable</th></tr></thead>
                      <tbody>
                        {entity.columns.map((c) => (
                          <tr key={c.name}><td>{c.name}</td><td>{c.type}</td><td>{c.nullable ? 'yes' : 'no'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

/** Copies, says so briefly, and where the browser has no clipboard to give (a plain-http origin, a refused
 *  permission) quietly selects the text instead, so Ctrl+C finishes the job. */
function CopyButton({ text, label = 'Copy', fallback }: { text: string; label?: string; fallback?: RefObject<HTMLElement> }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      selectText(fallback?.current ?? null)
    }
  }
  return <button type="button" className="button small" onClick={() => void copy()}>{copied ? 'Copied' : label}</button>
}

function selectText(el: HTMLElement | null) {
  const selection = el ? window.getSelection() : null
  if (!el || !selection) return
  const range = document.createRange()
  range.selectNodeContents(el)
  selection.removeAllRanges()
  selection.addRange(range)
}

function downloadText(text: string, fileName: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = fileName; a.click()
  URL.revokeObjectURL(url)
}
